import React, { useRef, useState, useEffect } from "react";
import { useTheme, Tooltip, Dialog, DialogContent, IconButton } from "@mui/material";
import { ArrowUpward, ArrowDownward, Close } from "@mui/icons-material";
import styled from "@xstyled/styled-components";
import { tokens } from "../../utils/theme";
import { useMarketOverviewData, isStatusRow } from "../../context/MarketOverviewContext";
import { copyTableAsJSON } from "../../utils/clipboardUtils";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Panel,
  PanelHeader,
  PanelTitle,
  TableWrapper,
  Table,
  Thead,
  Th,
  Tr,
  Td,
  TdSmall,
  ScrollRail,
  ScrollThumb,
  ScrollRailVertical,
  ScrollThumbVertical,
  Btn,
  BtnIcon
} from "../../components/SharedPageStyles";

const ImpactBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    if (props.level?.includes("High")) return "rgba(244, 67, 54, 0.15)";
    if (props.level?.includes("Medium")) return "rgba(255, 152, 0, 0.15)";
    return "rgba(76, 175, 80, 0.15)";
  }};
color: ${props => {
    if (props.level?.includes("High")) return tokens(props.theme.palette.mode).redAccent[500];
    if (props.level?.includes("Medium")) return tokens(props.theme.palette.mode).orangeAccent[500];
    return tokens(props.theme.palette.mode).greenAccent[500];
  }
  };
border: 1px solid ${props => {
    if (props.level?.includes("High")) return "rgba(244, 67, 54, 0.3)";
    if (props.level?.includes("Medium")) return "rgba(255, 152, 0, 0.3)";
    return "rgba(76, 175, 80, 0.3)";
  }
  };
`;

const HorizonSummaryGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.03)'};
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
  flex-shrink: 0;
`;

const HorizonPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 3px 9px;
  border-radius: 6px;
  font-family: 'JetBrains Mono', 'Roboto Mono', 'SFMono-Regular', Consolas, monospace;
  font-size: 11px;
  cursor: default;
  user-select: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${props => {
    if (props.$bias === 'Bullish') {
      if (props.$tier === 3) return props.theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.3)' : 'rgba(76, 175, 80, 0.2)';
      if (props.$tier === 2) return props.theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.2)' : 'rgba(76, 175, 80, 0.12)';
      return props.theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(76, 175, 80, 0.07)';
    }
    if (props.$bias === 'Bearish') {
      if (props.$tier === 3) return props.theme.palette.mode === 'dark' ? 'rgba(198, 40, 40, 0.3)' : 'rgba(244, 67, 54, 0.2)';
      if (props.$tier === 2) return props.theme.palette.mode === 'dark' ? 'rgba(198, 40, 40, 0.2)' : 'rgba(244, 67, 54, 0.12)';
      return props.theme.palette.mode === 'dark' ? 'rgba(198, 40, 40, 0.1)' : 'rgba(244, 67, 54, 0.07)';
    }
    return props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)';
  }};
  border: 1px solid ${props => {
    if (props.$medal === '🥇') return props.theme.palette.mode === 'dark' ? 'rgba(212, 175, 55, 0.28)' : 'rgba(180, 130, 20, 0.22)';
    if (props.$medal === '🥈') return props.theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.28)' : 'rgba(100, 116, 139, 0.22)';
    if (props.$medal === '🥉') return props.theme.palette.mode === 'dark' ? 'rgba(180, 120, 75, 0.28)' : 'rgba(160, 100, 60, 0.22)';
    if (props.$medal === '🎖️') return props.theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.32)' : 'rgba(79, 70, 229, 0.25)';
    if (props.$bias === 'Bullish') {
      if (props.$tier === 3) return props.theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.85)' : 'rgba(76, 175, 80, 0.7)';
      if (props.$tier === 2) return props.theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.55)' : 'rgba(76, 175, 80, 0.4)';
      return props.theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.28)' : 'rgba(76, 175, 80, 0.22)';
    }
    if (props.$bias === 'Bearish') {
      if (props.$tier === 3) return props.theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.85)' : 'rgba(244, 67, 54, 0.7)';
      if (props.$tier === 2) return props.theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.55)' : 'rgba(244, 67, 54, 0.4)';
      return props.theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.28)' : 'rgba(244, 67, 54, 0.22)';
    }
    return props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)';
  }};
  box-shadow: ${props => {
    if (props.$medal === '🥇') return props.theme.palette.mode === 'dark' ? '0 1px 4px rgba(212, 175, 55, 0.10)' : '0 1px 3px rgba(180, 130, 20, 0.08)';
    if (props.$medal === '🥈') return props.theme.palette.mode === 'dark' ? '0 1px 4px rgba(148, 163, 184, 0.08)' : '0 1px 3px rgba(100, 116, 139, 0.06)';
    if (props.$medal === '🥉') return props.theme.palette.mode === 'dark' ? '0 1px 4px rgba(180, 120, 75, 0.08)' : '0 1px 3px rgba(160, 100, 60, 0.06)';
    if (props.$medal === '🎖️') return props.theme.palette.mode === 'dark' ? '0 1px 4px rgba(99, 102, 241, 0.12)' : '0 1px 3px rgba(79, 70, 229, 0.08)';
    if (props.$bias === 'Bullish') {
      if (props.$tier === 3) return props.theme.palette.mode === 'dark' ? '0 0 10px rgba(76, 175, 80, 0.35)' : '0 0 8px rgba(76, 175, 80, 0.25)';
      if (props.$tier === 2) return props.theme.palette.mode === 'dark' ? '0 0 6px rgba(76, 175, 80, 0.18)' : 'none';
      return 'none';
    }
    if (props.$bias === 'Bearish') {
      if (props.$tier === 3) return props.theme.palette.mode === 'dark' ? '0 0 10px rgba(244, 67, 54, 0.35)' : '0 0 8px rgba(244, 67, 54, 0.25)';
      if (props.$tier === 2) return props.theme.palette.mode === 'dark' ? '0 0 6px rgba(244, 67, 54, 0.18)' : 'none';
      return 'none';
    }
    return 'none';
  }};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => {
    if (props.$medal === '🥇') return '0 2px 6px rgba(212, 175, 55, 0.18)';
    if (props.$medal === '🥈') return '0 2px 6px rgba(148, 163, 184, 0.15)';
    if (props.$medal === '🥉') return '0 2px 6px rgba(180, 120, 75, 0.15)';
    if (props.$medal === '🎖️') return '0 2px 6px rgba(99, 102, 241, 0.20)';
    if (props.$bias === 'Bullish') return '0 2px 10px rgba(76, 175, 80, 0.35)';
    if (props.$bias === 'Bearish') return '0 2px 10px rgba(244, 67, 54, 0.35)';
    return '0 2px 6px rgba(0, 0, 0, 0.18)';
  }};
  }
`;

const PillTf = styled.span`
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.2px;
  color: ${props => {
    if (props.$hasMedal) return props.theme.palette.mode === 'dark' ? '#f8fafc' : '#0f172a';
    return props.theme.palette.mode === 'dark' ? '#eceff1' : '#37474f';
  }};
`;

const PillIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-weight: 800;
  font-size: 11px;
  color: ${props => {
    if (props.$bias === 'Bullish') return tokens(props.theme.palette.mode).greenAccent[400];
    if (props.$bias === 'Bearish') return tokens(props.theme.palette.mode).redAccent[400];
    return tokens(props.theme.palette.mode).grey[300];
  }};
`;

const PillArrow = styled.span`
  font-size: ${props => props.$tier >= 3 ? '12px' : props.$tier === 2 ? '12.5px' : '13px'};
  letter-spacing: ${props => props.$tier >= 2 ? '-1.5px' : '0px'};
  font-weight: 800;
  line-height: 1;
`;

const getHorizonIntensity = (score) => {
  const absScore = Math.abs(score || 0);
  if (absScore === 0) {
    return {
      tier: 0,
      level: 'Neutral',
      arrows: '—',
      desc: 'Equilibrium or lack of directional catalyst'
    };
  }
  if (absScore <= 3) {
    return {
      tier: 1,
      level: score > 0 ? 'Bullish' : 'Bearish',
      arrows: score > 0 ? '↑' : '↓',
      desc: 'Background noise, technical structure takes 100% precedence'
    };
  }
  if (absScore <= 8) {
    return {
      tier: 2,
      level: score > 0 ? 'Strongly Bullish' : 'Strongly Bearish',
      arrows: score > 0 ? '↑↑' : '↓↓',
      desc: 'Asserted directional bias, favorable swing flow'
    };
  }
  return {
    tier: 3,
    level: score > 0 ? 'Extremely Bullish' : 'Extremely Bearish',
    arrows: score > 0 ? '↑↑↑' : '↓↓↓',
    desc: 'Macro tsunami / heavy consensus, do not trade against'
  };
};
const ScoreTooltip = ({ score, horizon, theme, children }) => {
  const isDark = theme.palette.mode === 'dark';
  const val = score ?? 0;
  const colors = tokens(theme.palette.mode);

  const scoreColor = val > 0
    ? colors.greenAccent[400]
    : val < 0
      ? colors.redAccent[400]
      : (isDark ? '#94a3b8' : '#64748b');

  const formattedVal = val > 0 ? `+${val}` : `${val}`;

  return (
    <Tooltip
      arrow
      placement="top"
      enterDelay={80}
      leaveDelay={50}
      PopperProps={{
        style: { zIndex: 2000 }
      }}
      title={
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '3px',
          fontFamily: "'JetBrains Mono', 'Roboto Mono', 'SFMono-Regular', Consolas, monospace",
          fontSize: '11px',
          letterSpacing: '0.3px',
          lineHeight: 1.2,
          padding: '1px 2px'
        }}>
          {horizon && (
            <span style={{
              color: isDark ? 'rgba(255, 255, 255, 0.75)' : 'rgba(15, 23, 42, 0.8)',
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.4px',
              borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
              paddingBottom: '3px',
              marginBottom: '2px'
            }}>
              {horizon}
            </span>
          )}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{
              color: isDark ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.55)',
              textTransform: 'uppercase',
              fontSize: '9px',
              letterSpacing: '0.8px',
              fontWeight: 700
            }}>
              Score
            </span>
            <span style={{
              color: scoreColor,
              fontWeight: 800,
              fontSize: '12px'
            }}>
              {formattedVal}
            </span>
          </div>
        </div>
      }
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: isDark ? 'rgba(22, 25, 37, 0.96)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            color: isDark ? colors.grey[100] : colors.grey[100],
            border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(15, 23, 42, 0.12)',
            boxShadow: isDark
              ? '0 6px 20px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.05)'
              : '0 6px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.04)',
            borderRadius: '6px',
            padding: '4px 8px',
          }
        },
        arrow: {
          sx: {
            color: isDark ? 'rgba(22, 25, 37, 0.96)' : 'rgba(255, 255, 255, 0.98)',
            '&::before': {
              border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(15, 23, 42, 0.12)',
            }
          }
        }
      }}
    >
      <span style={{ display: 'inline-flex' }}>
        {children}
      </span>
    </Tooltip>
  );
};

export const StrategyAdvisorModal = ({ row, onClose, theme }) => {
  if (!row) return null;

  const isDark = theme.palette.mode === 'dark';
  const colors = tokens(theme.palette.mode);

  const medal = row.medal || '🥇';
  const catalyst = row.catalyst || row.Catalyst;
  const name = row.name || '';
  const category = row.category || row.Category;
  const grade = row.grade || 'A+';
  const score = row.opportunity_score ?? 90;
  const setupType = (row.setup_type || 'TACTICAL_SETUP').replace(/_/g, ' ');
  const optimalTf = row.optimal_tf || 'H1-D1';
  const blueprint = row.blueprint;
  const horizonName = row.horizon || (optimalTf.includes('W1') || optimalTf.includes('D1-W1') ? 'Long Term' : optimalTf.includes('M15') ? 'Short Term' : 'Middle Term');
  const pricingText = row.pricing || (horizonName === 'Long Term' ? 'weeks to months' : horizonName === 'Short Term' ? 'hours to ~2 days' : '~2 days to weeks');

  // Multi-horizon score values
  const scoreSt = row.score_short_term ?? row.advisor_scores?.short_term;
  const scoreMt = row.score_middle_term ?? row.advisor_scores?.middle_term;
  const scoreLt = row.score_long_term ?? row.advisor_scores?.long_term;
  const statusSt = row.status_short_term || (scoreSt != null ? (scoreSt > 0 ? 'Bullish' : scoreSt < 0 ? 'Bearish' : 'Neutral') : null);
  const statusMt = row.status_middle_term || (scoreMt != null ? (scoreMt > 0 ? 'Bullish' : scoreMt < 0 ? 'Bearish' : 'Neutral') : null);
  const statusLt = row.status_long_term || (scoreLt != null ? (scoreLt > 0 ? 'Bullish' : scoreLt < 0 ? 'Bearish' : 'Neutral') : null);

  const scoreColor = score >= 85 ? colors.greenAccent[400] : score >= 70 ? colors.blueAccent[400] : colors.orangeAccent[400];

  const monoFont = "'JetBrains Mono', 'Fira Code', 'Roboto Mono', Consolas, monospace";
  const sansFont = "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

  const recStyle = row.recommended_style || (optimalTf === 'M15-H1' ? 'Day-Trading' : 'Swing');
  const isSwingRecommended = recStyle === 'Swing';
  const isDayRecommended = recStyle === 'Day-Trading' || recStyle === 'Day Trading';

  return (
    <Dialog
      open={!!row}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDark ? '#111420' : '#ffffff',
          color: isDark ? '#e6edf7' : '#0b1220',
          borderRadius: '16px',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(15, 23, 42, 0.12)',
          boxShadow: isDark
            ? '0 24px 60px rgba(0, 0, 0, 0.88), 0 0 0 1px rgba(255, 255, 255, 0.06)'
            : '0 20px 45px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
          fontFamily: sansFont,
        }
      }}
    >
      {/* Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
        background: isDark
          ? 'linear-gradient(180deg, rgba(24, 28, 42, 0.9) 0%, rgba(17, 20, 32, 0.8) 100%)'
          : 'linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.7) 100%)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <span style={{
              fontSize: '32px',
              lineHeight: 1,
              filter: medal === '🎖️'
                ? 'drop-shadow(0 0 10px rgba(99, 102, 241, 0.55))'
                : 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.45))'
            }}>
              {medal}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{
                fontFamily: monoFont,
                fontSize: '22px',
                fontWeight: 800,
                letterSpacing: '0.6px',
                color: isDark ? '#ffffff' : '#0f172a'
              }}>
                {catalyst}
              </span>
              {name && (
                <span style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: isDark ? '#94a3b8' : '#64748b'
                }}>
                  {name}
                </span>
              )}
              {category && (
                <span style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  background: isDark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.05)',
                  color: isDark ? '#94a3b8' : '#64748b',
                  letterSpacing: '0.6px'
                }}>
                  {category}
                </span>
              )}
              <span style={{
                fontSize: '10px',
                fontWeight: 700,
                textTransform: 'uppercase',
                padding: '2px 8px',
                borderRadius: '4px',
                background: medal === '🥇' ? 'rgba(212, 175, 55, 0.2)' : medal === '🥈' ? 'rgba(148, 163, 184, 0.2)' : medal === '🥉' ? 'rgba(180, 120, 75, 0.2)' : 'rgba(99, 102, 241, 0.2)',
                color: medal === '🥇' ? '#ffd700' : medal === '🥈' ? '#cbd5e1' : medal === '🥉' ? '#d97706' : '#818cf8',
                letterSpacing: '0.6px'
              }}>
                {medal === '🥇' ? 'Top 1 • Gold' : medal === '🥈' ? 'Top 2 • Silver' : medal === '🥉' ? 'Top 3 • Bronze' : 'Top 4 • Mention Honorable'}
              </span>
            </div>
          </div>
          <IconButton onClick={onClose} size="small" sx={{ color: isDark ? '#94a3b8' : '#64748b', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' } }}>
            <Close fontSize="small" />
          </IconButton>
        </div>
      </div>

      <DialogContent sx={{ padding: '20px 24px', fontFamily: sansFont }}>
        {/* Multi-Horizon Score Confluence Meter */}
        {(scoreSt != null || scoreMt != null || scoreLt != null) && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '10px',
            marginBottom: '16px',
            padding: '10px 14px',
            borderRadius: '10px',
            background: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.06)',
          }}>
            {/* Short-Term */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '8px' }}>
              <div>
                <div style={{ fontSize: '9.5px', fontWeight: 700, color: isDark ? '#7a8194' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Short Term (M15-H1)
                </div>
                <div style={{ fontSize: '11.5px', fontWeight: 600, color: isDark ? '#cbd5e1' : '#334155' }}>
                  {statusSt || 'Neutral'}
                </div>
              </div>
              <span style={{
                fontFamily: monoFont,
                fontSize: '12px',
                fontWeight: 800,
                padding: '2px 8px',
                borderRadius: '4px',
                background: scoreSt > 0 ? 'rgba(74, 222, 128, 0.15)' : scoreSt < 0 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(148, 163, 184, 0.15)',
                color: scoreSt > 0 ? colors.greenAccent[400] : scoreSt < 0 ? colors.redAccent[400] : (isDark ? '#94a3b8' : '#64748b'),
              }}>
                {scoreSt > 0 ? `+${scoreSt}` : scoreSt ?? '0'}
              </span>
            </div>

            {/* Middle-Term */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: '8px', paddingRight: '8px', borderLeft: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' }}>
              <div>
                <div style={{ fontSize: '9.5px', fontWeight: 700, color: isDark ? '#7a8194' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Middle Term (H1-D1)
                </div>
                <div style={{ fontSize: '11.5px', fontWeight: 600, color: isDark ? '#cbd5e1' : '#334155' }}>
                  {statusMt || 'Neutral'}
                </div>
              </div>
              <span style={{
                fontFamily: monoFont,
                fontSize: '12px',
                fontWeight: 800,
                padding: '2px 8px',
                borderRadius: '4px',
                background: scoreMt > 0 ? 'rgba(74, 222, 128, 0.15)' : scoreMt < 0 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(148, 163, 184, 0.15)',
                color: scoreMt > 0 ? colors.greenAccent[400] : scoreMt < 0 ? colors.redAccent[400] : (isDark ? '#94a3b8' : '#64748b'),
              }}>
                {scoreMt > 0 ? `+${scoreMt}` : scoreMt ?? '0'}
              </span>
            </div>

            {/* Long-Term */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: '8px', borderLeft: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' }}>
              <div>
                <div style={{ fontSize: '9.5px', fontWeight: 700, color: isDark ? '#7a8194' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Long Term (D1-W1)
                </div>
                <div style={{ fontSize: '11.5px', fontWeight: 600, color: isDark ? '#cbd5e1' : '#334155' }}>
                  {statusLt || 'Neutral'}
                </div>
              </div>
              <span style={{
                fontFamily: monoFont,
                fontSize: '12px',
                fontWeight: 800,
                padding: '2px 8px',
                borderRadius: '4px',
                background: scoreLt > 0 ? 'rgba(74, 222, 128, 0.15)' : scoreLt < 0 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(148, 163, 184, 0.15)',
                color: scoreLt > 0 ? colors.greenAccent[400] : scoreLt < 0 ? colors.redAccent[400] : (isDark ? '#94a3b8' : '#64748b'),
              }}>
                {scoreLt > 0 ? `+${scoreLt}` : scoreLt ?? '0'}
              </span>
            </div>
          </div>
        )}

        {/* KPI Strip */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
          marginBottom: '18px'
        }}>
          {/* Opportunity Score */}
          <div style={{
            padding: '12px 14px',
            borderRadius: '10px',
            background: isDark ? 'rgba(255, 255, 255, 0.025)' : 'rgba(0, 0, 0, 0.02)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.07)' : '1px solid rgba(0, 0, 0, 0.06)'
          }}>
            <div style={{
              fontSize: '10px',
              textTransform: 'uppercase',
              color: isDark ? '#7a8194' : '#64748b',
              letterSpacing: '0.8px',
              fontWeight: 700,
              marginBottom: '4px'
            }}>
              Opportunity Score
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <span style={{
                fontFamily: monoFont,
                fontSize: '22px',
                fontWeight: 800,
                color: scoreColor
              }}>
                {score}
              </span>
              <span style={{
                fontFamily: monoFont,
                fontSize: '12px',
                color: isDark ? '#64748b' : '#94a3b8'
              }}>
                /100
              </span>
              <span style={{
                fontSize: '11px',
                fontWeight: 700,
                marginLeft: '4px',
                color: colors.blueAccent[400],
                background: isDark ? 'rgba(56, 189, 248, 0.12)' : 'rgba(2, 132, 199, 0.1)',
                padding: '1px 6px',
                borderRadius: '4px'
              }}>
                {grade}
              </span>
            </div>
          </div>

          {/* Pattern / Setup */}
          <div style={{
            padding: '12px 14px',
            borderRadius: '10px',
            background: isDark ? 'rgba(255, 255, 255, 0.025)' : 'rgba(0, 0, 0, 0.02)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.07)' : '1px solid rgba(0, 0, 0, 0.06)'
          }}>
            <div style={{
              fontSize: '10px',
              textTransform: 'uppercase',
              color: isDark ? '#7a8194' : '#64748b',
              letterSpacing: '0.8px',
              fontWeight: 700,
              marginBottom: '4px'
            }}>
              Pattern / Setup
            </div>
            <div style={{
              fontSize: '12px',
              fontWeight: 700,
              color: colors.greenAccent[400],
              lineHeight: 1.3
            }}>
              {setupType}
            </div>
          </div>

          {/* Execution Timeframe */}
          <div style={{
            padding: '12px 14px',
            borderRadius: '10px',
            background: isDark ? 'rgba(255, 255, 255, 0.025)' : 'rgba(0, 0, 0, 0.02)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.07)' : '1px solid rgba(0, 0, 0, 0.06)'
          }}>
            <div style={{
              fontSize: '10px',
              textTransform: 'uppercase',
              color: isDark ? '#7a8194' : '#64748b',
              letterSpacing: '0.8px',
              fontWeight: 700,
              marginBottom: '4px'
            }}>
              Execution TF
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{
                fontFamily: monoFont,
                fontSize: '16px',
                fontWeight: 800,
                color: colors.primary[400],
                letterSpacing: '0.4px'
              }}>
                {optimalTf}
              </span>
              {horizonName && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '11px',
                  letterSpacing: '0.3px',
                  color: isDark ? '#93c5fd' : '#1d4ed8',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.16) 0%, rgba(37, 99, 235, 0.08) 100%)'
                    : 'linear-gradient(135deg, rgba(37, 99, 235, 0.09) 0%, rgba(59, 130, 246, 0.04) 100%)',
                  border: isDark ? '1px solid rgba(96, 165, 250, 0.3)' : '1px solid rgba(37, 99, 235, 0.22)',
                  padding: '2px 9px',
                  borderRadius: '16px',
                  boxShadow: isDark ? '0 1px 3px rgba(0, 0, 0, 0.25)' : 'none'
                }}>
                  <span style={{
                    width: '5px',
                    height: '5px',
                    borderRadius: '50%',
                    backgroundColor: isDark ? '#60a5fa' : '#2563eb',
                    boxShadow: isDark ? '0 0 6px rgba(96, 165, 250, 0.8)' : 'none',
                    display: 'inline-block'
                  }} />
                  <span style={{
                    fontStyle: 'italic',
                    fontWeight: 600,
                    fontFamily: sansFont
                  }}>
                    {horizonName}
                  </span>
                </span>
              )}
            </div>
            {pricingText && (
              <div style={{
                marginTop: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '3px 9px',
                borderRadius: '6px',
                background: isDark ? 'rgba(255, 255, 255, 0.035)' : 'rgba(0, 0, 0, 0.03)',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
                lineHeight: 1.2
              }}>
                <span style={{
                  fontSize: '9px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  fontWeight: 700,
                  color: isDark ? '#818cf8' : '#4f46e5'
                }}>
                  Pricing:
                </span>
                <span style={{
                  fontSize: '11px',
                  fontStyle: 'italic',
                  fontWeight: 500,
                  color: isDark ? '#e2e8f0' : '#1e293b'
                }}>
                  {pricingText}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Blueprint Dual Grid */}
        {blueprint && (
          <div>
            <div style={{
              fontSize: '11px',
              fontWeight: 800,
              textTransform: 'uppercase',
              color: isDark ? '#7a8194' : '#64748b',
              letterSpacing: '0.8px',
              marginBottom: '10px'
            }}>
              📐 Execution Blueprint
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              {/* Swing Column */}
              <div style={{
                padding: '14px 16px',
                borderRadius: '10px',
                background: isDark ? 'rgba(0, 0, 0, 0.25)' : 'rgba(248, 250, 252, 0.8)',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 700, fontSize: '13px', color: colors.primary[400] }}>
                    🌊 Swing Trading
                  </span>
                  {isSwingRecommended && (
                    <span style={{
                      fontSize: '9.5px',
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: isDark ? 'rgba(130, 170, 255, 0.2)' : 'rgba(37, 99, 235, 0.15)',
                      color: colors.primary[300],
                      letterSpacing: '0.4px'
                    }}>
                      RECOMMENDED
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>HTF Bias / Trend</span>
                    <span style={{
                      fontFamily: monoFont,
                      fontWeight: 700,
                      fontSize: '11.5px',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                      color: isDark ? '#f8fafc' : '#0f172a'
                    }}>
                      {blueprint.swing_trading?.chart_mapping_htf}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Observation Zone</span>
                    <span style={{
                      fontFamily: monoFont,
                      fontWeight: 700,
                      fontSize: '11.5px',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                      color: isDark ? '#f8fafc' : '#0f172a'
                    }}>
                      {blueprint.swing_trading?.zone_observation_mtf}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Entry Trigger</span>
                    <span style={{
                      fontFamily: monoFont,
                      fontWeight: 800,
                      fontSize: '11.5px',
                      color: colors.greenAccent[400],
                      background: isDark ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)',
                      padding: '1px 6px',
                      borderRadius: '4px'
                    }}>
                      {blueprint.swing_trading?.entry_trigger_ltf}
                    </span>
                  </div>
                </div>
              </div>

              {/* Day Trading Column */}
              <div style={{
                padding: '14px 16px',
                borderRadius: '10px',
                background: isDark ? 'rgba(0, 0, 0, 0.25)' : 'rgba(248, 250, 252, 0.8)',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 700, fontSize: '13px', color: colors.blueAccent[400] }}>
                    ⚡ Day Trading
                  </span>
                  {isDayRecommended && (
                    <span style={{
                      fontSize: '9.5px',
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: isDark ? 'rgba(56, 189, 248, 0.2)' : 'rgba(2, 132, 199, 0.15)',
                      color: colors.blueAccent[300],
                      letterSpacing: '0.4px'
                    }}>
                      RECOMMENDED
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>HTF Bias / Trend</span>
                    <span style={{
                      fontFamily: monoFont,
                      fontWeight: 700,
                      fontSize: '11.5px',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                      color: isDark ? '#f8fafc' : '#0f172a'
                    }}>
                      {blueprint.day_trading?.chart_mapping_htf}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Observation Zone</span>
                    <span style={{
                      fontFamily: monoFont,
                      fontWeight: 700,
                      fontSize: '11.5px',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                      color: isDark ? '#f8fafc' : '#0f172a'
                    }}>
                      {blueprint.day_trading?.zone_observation_mtf}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Entry Trigger</span>
                    <span style={{
                      fontFamily: monoFont,
                      fontWeight: 800,
                      fontSize: '11.5px',
                      color: colors.greenAccent[400],
                      background: isDark ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)',
                      padding: '1px 6px',
                      borderRadius: '4px'
                    }}>
                      {blueprint.day_trading?.entry_trigger_ltf}
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};



const EditableInput = styled.input`
width: 100%;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
  color: inherit;
  padding: 4px;
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}33;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(150, 150, 150, 0.2)'};
  }
`;

const EditableSelect = styled.select`
  width: 100%;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
  color: inherit;
  padding: 4px;
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}33;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(150, 150, 150, 0.2)'};
  }
  
  option {
    background: ${props => props.theme.palette.mode === 'dark' ? '#1e2130' : '#ffffff'};
    color: inherit;
  }
`;

const EditableTextArea = styled.textarea`
  width: 100%;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
  color: inherit;
  padding: 4px;
  padding-right: 36px; /* Space for arrows */
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  resize: vertical;
  min-height: 60px;
  
  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}33;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(150, 150, 150, 0.2)'};
  }
`;

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ScrollControls = styled.div`
  position: absolute;
  right: 4px;
  top: 4px;
  bottom: 4px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2px;
  pointer-events: none;
`;

const ScrollButton = styled.div`
  pointer-events: auto;
  cursor: pointer;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 2px;
  transition: all 0.2s;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.8)'};

  &:hover {
    color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.95)'};
  }
  
  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;

const ModalOverlay = styled.div`
position: fixed;
top: 0;
left: 0;
right: 0;
bottom: 0;
background: rgba(0, 0, 0, 0.7);
display: flex;
justify-content: center;
align-items: center;
z-index: 1100;
`;

const ModalContent = styled.div`
background: #1e2130;
padding: 24px;
border-radius: 8px;
width: 400px;
border: 1px solid rgba(255, 255, 255, 0.1);
box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
text-align: center;
`;

const ButtonRow = styled.div`
display: flex;
justify-content: center;
gap: 12px;
margin-top: 24px;
`;

const EditableCell = ({ value, onChange, type = "text", options = [], disabled = false }) => {
  const [localValue, setLocalValue] = useState(value);
  const [showScroll, setShowScroll] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (textareaRef.current) {
      const { scrollHeight, clientHeight } = textareaRef.current;
      setShowScroll(scrollHeight > clientHeight);
    }
  }, [localValue]);

  const handleBlur = () => {
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  const handleScroll = (direction) => {
    if (textareaRef.current) {
      textareaRef.current.scrollTop += direction * 20;
    }
  };

  if (type === "select") {
    return (
      <EditableSelect
        disabled={disabled}
        value={localValue || ""}
        onChange={(e) => {
          setLocalValue(e.target.value);
          onChange(e.target.value);
        }}
        onClick={(e) => e.stopPropagation()}
        onBlur={handleBlur}
      >
        {options.map((opt, idx) => (
          <option key={idx} value={opt.value !== undefined ? opt.value : opt}>
            {opt.label !== undefined ? opt.label : opt}
          </option>
        ))}
      </EditableSelect>
    );
  }

  if (type === "textarea") {
    return (
      <TextAreaWrapper>
        <EditableTextArea
          disabled={disabled}
          ref={textareaRef}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onClick={(e) => e.stopPropagation()}
        />
        {showScroll && (
          <ScrollControls>
            <ScrollButton onClick={(e) => { e.stopPropagation(); handleScroll(-1); }} title="Scroll Up">
              <svg viewBox="0 0 24 24">
                <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
              </svg>
            </ScrollButton>
            <ScrollButton onClick={(e) => { e.stopPropagation(); handleScroll(1); }} title="Scroll Down">
              <svg viewBox="0 0 24 24">
                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
              </svg>
            </ScrollButton>
          </ScrollControls>
        )}
      </TextAreaWrapper>
    );
  }
  return (
    <EditableInput
      disabled={disabled}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={handleBlur}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const SectionHeaderRow = styled(Tr)`
background: rgba(30, 33, 48, 0.8);
border-bottom: 2px solid rgba(128, 203, 196, 0.2);
`;

const SectionHeaderCell = styled(Td)`
font-size: 14px;
font-weight: 400;
color: ${props => props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600]};
padding: 12px 16px;
text-transform: uppercase;
letter-spacing: 1px;
background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(15, 17, 26, 0.95)' : 'rgba(255, 255, 255, 0.9)'};
`;

const ConclusionContainer = styled.div`
display: flex;
align-items: center;
gap: 16px;
padding: 8px 4px;
`;

const ConclusionText = styled.span`
color: ${props => props.theme.palette.mode === 'dark' ? '#b0bec5' : tokens(props.theme.palette.mode).grey[300]};
font-style: italic;
font-size: 13px;
line-height: 1.4;
`;

const SortHeader = ({ label, column, sortColumn, sortDirection, onSort, align = 'center' }) => {
  const isSorted = sortColumn === column && Boolean(sortDirection);
  return (
    <div
      onClick={() => onSort(column)}
      title={`Sort by ${label} (Desc / Asc / Neutral)`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: align === 'left' ? 'flex-start' : 'center',
        gap: '3px',
        cursor: 'pointer',
        userSelect: 'none',
        verticalAlign: 'middle',
        lineHeight: 1
      }}
    >
      <span>{label}</span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '12px',
          height: '12px',
          visibility: isSorted ? 'visible' : 'hidden',
          flexShrink: 0
        }}
      >
        {sortDirection === 'desc' && (
          <ArrowDownward style={{ fontSize: '11px', width: '11px', height: '11px', opacity: 0.85 }} />
        )}
        {sortDirection === 'asc' && (
          <ArrowUpward style={{ fontSize: '11px', width: '11px', height: '11px', opacity: 0.85 }} />
        )}
      </span>
    </div>
  );
};

const CheckboxContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;
  position: relative;
  z-index: 10;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  border: 0;
  clip: rect(0 0 0 0);
  clippath: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const StyledCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: ${props => props.checked || props.indeterminate
    ? tokens(props.theme.palette.mode).blueAccent[500]
    : 'transparent'};
  border: 2px solid ${props => props.checked || props.indeterminate
    ? tokens(props.theme.palette.mode).blueAccent[500]
    : tokens(props.theme.palette.mode).grey[500]};
  border-radius: 4px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}40; 
  }

  &:hover {
     border-color: ${props => props.checked || props.indeterminate
    ? tokens(props.theme.palette.mode).blueAccent[600]
    : tokens(props.theme.palette.mode).blueAccent[400]};
  }
`;

const Icon = styled.svg`
  fill: none;
  stroke: ${props => props.theme.palette.mode === 'dark' ? '#000' : '#fff'};
  stroke-width: 3px;
  width: 12px;
  height: 12px;
  visibility: ${props => (props.visible ? 'visible' : 'hidden')};
`;

const CustomCheckbox = ({ checked, indeterminate, onChange, ...props }) => (
  <CheckboxContainer onClick={(e) => {
    e.stopPropagation();
    onChange({ target: { checked: !checked } });
  }}>
    <HiddenCheckbox checked={checked} onChange={onChange} {...props} />
    <StyledCheckbox checked={checked} indeterminate={indeterminate}>
      {indeterminate ? (
        <Icon viewBox="0 0 24 24" visible={true}>
          <line x1="4" y1="12" x2="20" y2="12" />
        </Icon>
      ) : (
        <Icon viewBox="0 0 24 24" visible={checked}>
          <polyline points="20 6 9 17 4 12" />
        </Icon>
      )}
    </StyledCheckbox>
  </CheckboxContainer>
);

const MarketOverviewTable = ({ data, title, defaultRowData = {}, defaultCatalysts = [], customSortOrder = [] }) => {
  console.log("MarketOverviewTable rendered with data:", data);

  const theme = useTheme();
  const { rows, deleteRow, deleteRows, updateRow, getOriginalIndex, setAllData, addRow, resetData } = useMarketOverviewData();
  const [selectedAdvisorRow, setSelectedAdvisorRow] = useState(null);


  // Group data by Instrument (Catalyst)
  const groupedData = data.reduce((acc, row) => {
    const key = row.Catalyst || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {});

  // Ensure default catalysts are present
  defaultCatalysts.forEach(catalyst => {
    if (!groupedData[catalyst]) {
      groupedData[catalyst] = [];
    }
  });

  console.log("Grouped Data:", groupedData);

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId !== destination.droppableId) return; // Only allow reordering within the same list
    if (source.index === destination.index) return;

    // Parse droppableId to get type and instrument
    // Format: type-instrument (e.g., "analysis-EURUSD", "conclusion-EURUSD")
    const [type, ...instrumentParts] = source.droppableId.split('-');
    const instrument = instrumentParts.join('-');

    // Get the relevant subset of rows
    const groupRows = groupedData[instrument] || [];
    let subset = [];
    if (type === 'analysis') {
      subset = groupRows.filter(r => !isStatusRow(r));
    } else if (type === 'conclusion') {
      subset = groupRows.filter(r => isStatusRow(r));
    }

    if (subset.length === 0) return;

    // Get global indices of these rows
    const globalIndices = subset.map(r => getOriginalIndex(r));

    // Reorder the subset locally
    const newSubset = Array.from(subset);
    const [moved] = newSubset.splice(source.index, 1);
    newSubset.splice(destination.index, 0, moved);

    // Construct new global rows array
    const newGlobalRows = Array.from(rows);
    globalIndices.forEach((globalIndex, i) => {
      newGlobalRows[globalIndex] = newSubset[i];
    });

    setAllData(newGlobalRows);
  };

  const [expandedGroups, setExpandedGroups] = useState({});
  const [sortColumn, setSortColumn] = useState('Date'); // 'Date' | 'Impact' | 'Direction' | 'Horizon' | null
  const [sortDirection, setSortDirection] = useState('desc'); // 'desc' | 'asc' | null

  const handleSort = (column) => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('desc');
    } else {
      if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else if (sortDirection === 'asc') {
        setSortColumn(null);
        setSortDirection(null);
      } else {
        setSortDirection('desc');
      }
    }
  };

  const toggleGroup = (instrument) => {
    setExpandedGroups(prev => ({
      ...prev,
      [instrument]: !prev[instrument]
    }));
    if (isEditMode) {
      // Don't clear selected rows when toggling group
    }
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [originalDataSnapshot, setOriginalDataSnapshot] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Context Menu State
  const [rowToDelete, setRowToDelete] = useState(null);

  const tableWrapperRef = useRef(null);
  const scrollRailXRef = useRef(null);
  const scrollRailYRef = useRef(null);

  // Horizontal Scroll State
  const [thumbWidth, setThumbWidth] = useState("38%");
  const [thumbLeft, setThumbLeft] = useState("0%");
  const [showScrollX, setShowScrollX] = useState(false);
  const isDraggingX = useRef(false);
  const startX = useRef(0);
  const startScrollLeft = useRef(0);

  // Vertical Scroll State
  const [thumbHeight, setThumbHeight] = useState("38%");
  const [thumbTop, setThumbTop] = useState("0%");
  const [showScrollY, setShowScrollY] = useState(false);
  const isDraggingY = useRef(false);
  const startY = useRef(0);
  const startScrollTop = useRef(0);

  const handleScroll = () => {
    if (tableWrapperRef.current) {
      if (isDraggingX.current || isDraggingY.current) return;

      const {
        scrollLeft, scrollWidth, clientWidth,
        scrollTop, scrollHeight, clientHeight
      } = tableWrapperRef.current;

      const maxScrollLeft = scrollWidth - clientWidth;
      if (maxScrollLeft > 0) {
        const scrollPercentageX = (scrollLeft / maxScrollLeft) * 100;
        const maxLeft = 100 - parseFloat(thumbWidth);
        const left = (scrollPercentageX / 100) * maxLeft;
        setThumbLeft(`${left}% `);
      }

      const maxScrollTop = scrollHeight - clientHeight;
      if (maxScrollTop > 0) {
        const scrollPercentageY = (scrollTop / maxScrollTop) * 100;
        const maxTop = 100 - parseFloat(thumbHeight);
        const top = (scrollPercentageY / 100) * maxTop;
        setThumbTop(`${top}% `);
      }
    }
  };

  useEffect(() => {
    const checkScroll = () => {
      if (tableWrapperRef.current) {
        const { scrollWidth, clientWidth, scrollHeight, clientHeight } = tableWrapperRef.current;
        const hasScrollX = scrollWidth > clientWidth;
        setShowScrollX(hasScrollX);
        if (hasScrollX) {
          const widthPerc = (clientWidth / scrollWidth) * 100;
          const finalWidth = Math.max(widthPerc, 10);
          setThumbWidth(`${finalWidth}% `);
        }
        const hasScrollY = scrollHeight > clientHeight;
        setShowScrollY(hasScrollY);
        if (hasScrollY) {
          const heightPerc = (clientHeight / scrollHeight) * 100;
          const finalHeight = Math.max(heightPerc, 10);
          setThumbHeight(`${finalHeight}% `);
        }
      }
    };
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [data]);

  const handleMouseDownX = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingX.current = true;
    startX.current = e.clientX;
    startScrollLeft.current = tableWrapperRef.current.scrollLeft;
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseDownY = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingY.current = true;
    startY.current = e.clientY;
    startScrollTop.current = tableWrapperRef.current.scrollTop;
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (isDraggingX.current && tableWrapperRef.current && scrollRailXRef.current) {
      e.preventDefault();
      const { scrollWidth, clientWidth } = tableWrapperRef.current;
      const railWidth = scrollRailXRef.current.clientWidth;
      const thumbWidthPx = (parseFloat(thumbWidth) / 100) * railWidth;
      const maxScrollLeft = scrollWidth - clientWidth;
      const availableRail = railWidth - thumbWidthPx;
      const deltaX = e.clientX - startX.current;
      const scrollRatio = maxScrollLeft / availableRail;
      tableWrapperRef.current.scrollLeft = startScrollLeft.current + (deltaX * scrollRatio);
      const scrollPercentageX = (tableWrapperRef.current.scrollLeft / maxScrollLeft) * 100;
      const maxLeft = 100 - parseFloat(thumbWidth);
      const left = (scrollPercentageX / 100) * maxLeft;
      setThumbLeft(`${left}% `);
    }
    if (isDraggingY.current && tableWrapperRef.current && scrollRailYRef.current) {
      e.preventDefault();
      const { scrollHeight, clientHeight } = tableWrapperRef.current;
      const railHeight = scrollRailYRef.current.clientHeight;
      const thumbHeightPx = (parseFloat(thumbHeight) / 100) * railHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      const availableRail = railHeight - thumbHeightPx;
      const deltaY = e.clientY - startY.current;
      const scrollRatio = maxScrollTop / availableRail;
      tableWrapperRef.current.scrollTop = startScrollTop.current + (deltaY * scrollRatio);
      const scrollPercentageY = (tableWrapperRef.current.scrollTop / maxScrollTop) * 100;
      const maxTop = 100 - parseFloat(thumbHeight);
      const top = (scrollPercentageY / 100) * maxTop;
      setThumbTop(`${top}% `);
    }
  };

  const handleMouseUp = () => {
    isDraggingX.current = false;
    isDraggingY.current = false;
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const confirmDelete = () => {
    if (rowToDelete) {
      // If rowToDelete is an array (from multiple selection), handle it
      if (Array.isArray(rowToDelete)) {
        const indicesToDelete = rowToDelete.map(row => getOriginalIndex(row)).filter(idx => idx !== -1);
        // Call bulk delete
        deleteRows(indicesToDelete);
      } else {
        // Single row deletion
        const index = getOriginalIndex(rowToDelete);
        if (index !== -1) {
          deleteRow(index);
        }
      }
      setRowToDelete(null);
      setSelectedRows(new Set());
    }
  };

  const handleCellUpdate = (row, field, newValue) => {
    const index = getOriginalIndex(row);
    if (index !== -1) {
      updateRow(index, { ...row, [field]: newValue });
    }
  };

  const handleEditModeToggle = () => {
    if (!isEditMode) {
      // Entering Edit Mode: Snapshot current data
      setOriginalDataSnapshot(JSON.parse(JSON.stringify(rows)));
      setIsEditMode(true);
      setSelectedRows(new Set());
    } else {
      // Saving (default behavior of button if it was just a toggle)
      // But we will separate Save and Cancel
      setIsEditMode(false);
      setOriginalDataSnapshot(null);
      setSelectedRows(new Set());
    }
  };

  const handleSave = () => {
    setIsEditMode(false);
    setOriginalDataSnapshot(null);
    setSelectedRows(new Set());
    if (resetData) resetData();
  };

  const handleCancel = () => {
    if (originalDataSnapshot) {
      setAllData(originalDataSnapshot);
    }
    setIsEditMode(false);
    setOriginalDataSnapshot(null);
    setSelectedRows(new Set());
  };

  const handleRowClick = (row) => {
    if (isEditMode) {
      const newSelected = new Set(selectedRows);
      if (newSelected.has(row)) {
        newSelected.delete(row);
      } else {
        newSelected.add(row);
      }
      setSelectedRows(newSelected);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(new Set(data));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleDeleteSelected = () => {
    if (selectedRows.size > 0) {
      setRowToDelete(Array.from(selectedRows));
    }
  };

  // Helper to sort keys based on customSortOrder
  const getSortedKeys = (keys) => {
    if (!customSortOrder || customSortOrder.length === 0) return keys.sort();

    return keys.sort((a, b) => {
      const indexA = customSortOrder.indexOf(a);
      const indexB = customSortOrder.indexOf(b);

      // If both are in the list, sort by index
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;

      // If only A is in the list, it comes first
      if (indexA !== -1) return -1;

      // If only B is in the list, it comes second
      if (indexB !== -1) return 1;

      // If neither is in the list, sort alphabetically
      return a.localeCompare(b);
    });
  };

  const sortedKeys = getSortedKeys(Object.keys(groupedData));

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>{title}</PanelTitle>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <BtnIcon onClick={() => copyTableAsJSON(data)} title="Copy Table JSON">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px' }}>
              <rect x="9" y="9" width="13" height="13" rx="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </BtnIcon>
          {isEditMode ? (
            <>
              <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
              <Btn primary onClick={handleSave}>
                Save
              </Btn>
              <Btn danger onClick={handleCancel}>
                Cancel
              </Btn>
            </>
          ) : (
            <BtnIcon onClick={handleEditModeToggle} title="Edit Mode">
              ✎
            </BtnIcon>
          )}
        </div>
      </PanelHeader>
      <TableWrapper ref={tableWrapperRef} onScroll={handleScroll} style={{ overflowX: 'auto', width: '100%' }}>
        <Table>
          <Thead>
            <tr>
              {isEditMode && (
                <Th style={{ width: '40px', textAlign: 'center' }}>
                  <CustomCheckbox
                    checked={data.length > 0 && selectedRows.size === data.length}
                    indeterminate={selectedRows.size > 0 && selectedRows.size < data.length}
                    onChange={handleSelectAll}
                  />
                </Th>
              )}
              {isEditMode && <Th style={{ width: '50px' }}>ID</Th>}

              <Th style={{ minWidth: '480px' }}>Headline</Th>
              <Th style={{ width: '95px', minWidth: '95px' }}>
                <SortHeader label="Date" column="Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} align="left" />
              </Th>
              <Th style={{ minWidth: '440px' }}>Interpretation</Th>
              <Th style={{ width: '85px', minWidth: '85px', textAlign: 'center' }}>
                <SortHeader label="Impact" column="Impact" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} align="center" />
              </Th>
              <Th style={{ width: '80px', minWidth: '80px', textAlign: 'center' }}>
                <SortHeader label="Direction" column="Direction" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} align="center" />
              </Th>
              <Th style={{ width: '80px', minWidth: '80px', textAlign: 'center' }}>
                <SortHeader label="Horizon" column="Horizon" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} align="center" />
              </Th>
              <Th style={{ width: '110px', minWidth: '110px' }}>Assets</Th>
              <Th style={{ width: '110px', minWidth: '110px' }}>Sources</Th>
            </tr>
          </Thead>
          <DragDropContext onDragEnd={onDragEnd}>
            {sortedKeys.length > 0 ? (
              sortedKeys.map((instrument) => {
                const grpRows = groupedData[instrument];
                const analysisRows = grpRows.filter(r => !isStatusRow(r)).sort((a, b) => {
                  // Impact Priority Helper (VERY HIGH: 4 > HIGH: 3 > MEDIUM: 2 > LOW: 1)
                  const impactValue = (impact) => {
                    const normalized = (impact || '').trim().toUpperCase();
                    switch (normalized) {
                      case 'VERY HIGH': return 4;
                      case 'HIGH': return 3;
                      case 'MEDIUM': return 2;
                      case 'LOW': return 1;
                      default: return 0;
                    }
                  };

                  // Direction Priority Helper (Bullish: 1 > Neutral: 0 > Bearish: -1)
                  const directionValue = (dir) => {
                    const normalized = (dir || '').trim().toUpperCase();
                    if (normalized === 'BULLISH' || normalized === 'HAUSSIER' || normalized === 'BUY' || normalized === 'LONG') return 1;
                    if (normalized === 'BEARISH' || normalized === 'BAISSIER' || normalized === 'SELL' || normalized === 'SHORT') return -1;
                    return 0;
                  };

                  // Horizon Priority Helper (Long Term: 3 > Middle Term: 2 > Short Term: 1)
                  const horizonValue = (h) => {
                    const normalized = (h || '').trim().toUpperCase();
                    if (normalized.includes('LONG')) return 3;
                    if (normalized.includes('MID') || normalized.includes('MIDDLE')) return 2;
                    if (normalized.includes('SHORT')) return 1;
                    return 0;
                  };

                  // Primary Sort (if active column selected)
                  if (sortColumn && sortDirection) {
                    let comparison = 0;

                    if (sortColumn === 'Impact') {
                      const valA = impactValue(a.Impact);
                      const valB = impactValue(b.Impact);
                      if (valA === 0 && valB !== 0) comparison = 1;
                      else if (valB === 0 && valA !== 0) comparison = -1;
                      else comparison = sortDirection === 'asc' ? valA - valB : valB - valA;
                    } else if (sortColumn === 'Direction') {
                      const valA = directionValue(a.Direction);
                      const valB = directionValue(b.Direction);
                      comparison = sortDirection === 'asc' ? valA - valB : valB - valA;
                    } else if (sortColumn === 'Horizon' || sortColumn === 'Duration') {
                      const valA = horizonValue(a.Horizon || a.Duration);
                      const valB = horizonValue(b.Horizon || b.Duration);
                      if (valA === 0 && valB !== 0) comparison = 1;
                      else if (valB === 0 && valA !== 0) comparison = -1;
                      else comparison = sortDirection === 'asc' ? valA - valB : valB - valA;
                    } else if (sortColumn === 'Date') {
                      const dateA = new Date(a.Date || 0).getTime();
                      const dateB = new Date(b.Date || 0).getTime();
                      comparison = sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
                    }

                    if (comparison !== 0) return comparison;
                  }

                  // Default / Secondary Tie-Breaker: Date descending (newest first)
                  const dateA = new Date(a.Date || 0).getTime();
                  const dateB = new Date(b.Date || 0).getTime();
                  return dateB - dateA;
                });
                const conclusionRows = grpRows.filter(r => isStatusRow(r));
                const isExpanded = expandedGroups[instrument] || isEditMode;

                return (
                  <React.Fragment key={instrument}>
                    {/* Instrument Header */}
                    <tbody>
                      <SectionHeaderRow
                        onClick={() => toggleGroup(instrument)}
                        style={{ cursor: 'pointer' }}
                      >
                        <SectionHeaderCell colSpan={isEditMode ? 10 : 8}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {instrument}
                              <BtnIcon
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyTableAsJSON(groupedData[instrument]);
                                }}
                                title={`Copy ${instrument} data`}
                                style={{ width: '24px', height: '24px', padding: '4px', opacity: 0.7 }}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '14px', height: '14px' }}>
                                  <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                </svg>
                              </BtnIcon>
                              {isEditMode && (
                                <>
                                  <BtnIcon
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      addRow({ ...defaultRowData, Catalyst: instrument });
                                    }}
                                    title={`Add row to ${instrument}`}
                                    style={{ width: '24px', height: '24px', padding: '4px', opacity: 0.7, color: '#4caf50' }}
                                  >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '14px', height: '14px' }}>
                                      <line x1="12" y1="5" x2="12" y2="19"></line>
                                      <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                  </BtnIcon>
                                  <BtnIcon
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const groupSelectedRows = Array.from(selectedRows).filter(r => r.Catalyst === instrument);
                                      if (groupSelectedRows.length > 0) {
                                        setRowToDelete(groupSelectedRows);
                                      }
                                    }}
                                    disabled={Array.from(selectedRows).filter(r => r.Catalyst === instrument).length === 0}
                                    title={`Delete selected rows in ${instrument}`}
                                    style={{
                                      width: '24px', height: '24px', padding: '4px',
                                      opacity: Array.from(selectedRows).filter(r => r.Catalyst === instrument).length === 0 ? 0.3 : 0.7,
                                      color: '#f44336'
                                    }}
                                  >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '14px', height: '14px' }}>
                                      <path d="M3 6h18"></path>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                      <line x1="10" y1="11" x2="10" y2="17"></line>
                                      <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                  </BtnIcon>
                                </>
                              )}
                            </div>
                            <span style={{ fontSize: '12px', opacity: 0.7 }}>
                              {isExpanded ? '▼' : '▶'}
                            </span>
                          </div>
                        </SectionHeaderCell>
                      </SectionHeaderRow>
                    </tbody>

                    {/* Conclusion Rows (Summary) - Always Visible */}
                    <Droppable droppableId={`conclusion-${instrument}`}>
                      {(provided) => (
                        <tbody ref={provided.innerRef} {...provided.droppableProps}>
                          {conclusionRows.length === 0 ? (
                            <Tr>
                              <Td colSpan={isEditMode ? 10 : 8} style={{ textAlign: "center", fontStyle: "italic", color: "#607d8b", padding: "12px" }}>
                                No Status
                              </Td>
                            </Tr>
                          ) : (
                            conclusionRows.map((row, index) => {
                              return (
                                <Draggable key={`conclusion-${instrument}-${index}`} draggableId={`conclusion-${instrument}-${index}`} index={index} isDragDisabled={!isEditMode}>
                                  {(provided, snapshot) => (
                                    <Tr
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      onClick={() => toggleGroup(instrument)}
                                      style={{
                                        ...provided.draggableProps.style,
                                        background: isEditMode && selectedRows.has(row) ? 'rgba(128, 203, 196, 0.1)' : snapshot.isDragging ? 'rgba(128, 203, 196, 0.15)' : 'rgba(128, 203, 196, 0.05)',
                                        borderTop: '1px solid rgba(128, 203, 196, 0.2)',
                                        cursor: isEditMode ? 'grab' : 'pointer',
                                        display: 'table-row'
                                      }}
                                    >
                                      {isEditMode && (
                                        <Td style={{ width: '40px', textAlign: 'center', verticalAlign: 'middle', borderBottom: '1px solid rgba(128, 203, 196, 0.1)' }}>
                                          <CustomCheckbox
                                            checked={selectedRows.has(row)}
                                            onChange={() => handleRowClick(row)}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </Td>
                                      )}
                                      {isEditMode && (
                                        <Td style={{ width: '50px', padding: '12px 8px', verticalAlign: 'middle', textAlign: 'center', borderBottom: '1px solid rgba(128, 203, 196, 0.1)' }}>
                                          <TdSmall style={{ opacity: 0.5 }}>{row.id}</TdSmall>
                                        </Td>
                                      )}
                                      <Td colSpan={isEditMode ? 8 : 8} style={{ padding: '12px 16px' }}>
                                        <ConclusionContainer>
                                          {(() => {
                                            const intSt = getHorizonIntensity(row.score_short_term);
                                            const intMt = getHorizonIntensity(row.score_middle_term);
                                            const intLt = getHorizonIntensity(row.score_long_term);

                                            const activeMedal = row.medal;
                                            const activeTf = row.optimal_tf;

                                            const medalSt = (activeMedal && activeTf === 'M15-H1') ? activeMedal : null;
                                            const medalMt = (activeMedal && activeTf === 'H1-D1') ? activeMedal : null;
                                            const medalLt = (activeMedal && activeTf === 'D1-W1') ? activeMedal : null;

                                            return (
                                              <HorizonSummaryGroup theme={theme}>
                                                <ScoreTooltip score={row.score_short_term} horizon="Short Term · M15-H1" theme={theme}>
                                                  <HorizonPill
                                                    $bias={row.status_short_term || 'Neutral'}
                                                    $tier={intSt.tier}
                                                    $medal={medalSt}
                                                    theme={theme}
                                                    onClick={(e) => {
                                                      if (medalSt) {
                                                        e.stopPropagation();
                                                        setSelectedAdvisorRow(row);
                                                      }
                                                    }}
                                                    style={{ cursor: medalSt ? 'pointer' : 'default' }}
                                                  >
                                                    <PillTf theme={theme} $hasMedal={!!medalSt}>
                                                      {medalSt ? (
                                                        <span
                                                          style={{
                                                            cursor: 'pointer',
                                                            marginRight: '4px',
                                                            display: 'inline-block',
                                                            filter: medalSt === '🎖️' ? 'drop-shadow(0 0 4px rgba(99, 102, 241, 0.75))' : 'drop-shadow(0 0 3px rgba(255, 215, 0, 0.6))'
                                                          }}
                                                          title="Click to view Strategy Advisor Blueprint"
                                                        >
                                                          {medalSt}
                                                        </span>
                                                      ) : ''}M15-H1
                                                    </PillTf>
                                                    <PillIndicator $bias={row.status_short_term || 'Neutral'} theme={theme}>
                                                      <PillArrow $tier={intSt.tier}>{intSt.arrows}</PillArrow>
                                                    </PillIndicator>
                                                  </HorizonPill>
                                                </ScoreTooltip>

                                                <ScoreTooltip score={row.score_middle_term} horizon="Middle Term · H1-D1" theme={theme}>
                                                  <HorizonPill
                                                    $bias={row.status_middle_term || 'Neutral'}
                                                    $tier={intMt.tier}
                                                    $medal={medalMt}
                                                    theme={theme}
                                                    onClick={(e) => {
                                                      if (medalMt) {
                                                        e.stopPropagation();
                                                        setSelectedAdvisorRow(row);
                                                      }
                                                    }}
                                                    style={{ cursor: medalMt ? 'pointer' : 'default' }}
                                                  >
                                                    <PillTf theme={theme} $hasMedal={!!medalMt}>
                                                      {medalMt ? (
                                                        <span
                                                          style={{
                                                            cursor: 'pointer',
                                                            marginRight: '4px',
                                                            display: 'inline-block',
                                                            filter: medalMt === '🎖️' ? 'drop-shadow(0 0 4px rgba(99, 102, 241, 0.75))' : 'drop-shadow(0 0 3px rgba(255, 215, 0, 0.6))'
                                                          }}
                                                          title="Click to view Strategy Advisor Blueprint"
                                                        >
                                                          {medalMt}
                                                        </span>
                                                      ) : ''}H1-D1
                                                    </PillTf>
                                                    <PillIndicator $bias={row.status_middle_term || 'Neutral'} theme={theme}>
                                                      <PillArrow $tier={intMt.tier}>{intMt.arrows}</PillArrow>
                                                    </PillIndicator>
                                                  </HorizonPill>
                                                </ScoreTooltip>

                                                <ScoreTooltip score={row.score_long_term} horizon="Long Term · D1-W1" theme={theme}>
                                                  <HorizonPill
                                                    $bias={row.status_long_term || 'Neutral'}
                                                    $tier={intLt.tier}
                                                    $medal={medalLt}
                                                    theme={theme}
                                                    onClick={(e) => {
                                                      if (medalLt) {
                                                        e.stopPropagation();
                                                        setSelectedAdvisorRow(row);
                                                      }
                                                    }}
                                                    style={{ cursor: medalLt ? 'pointer' : 'default' }}
                                                  >
                                                    <PillTf theme={theme} $hasMedal={!!medalLt}>
                                                      {medalLt ? (
                                                        <span
                                                          style={{
                                                            cursor: 'pointer',
                                                            marginRight: '4px',
                                                            display: 'inline-block',
                                                            filter: medalLt === '🎖️' ? 'drop-shadow(0 0 4px rgba(99, 102, 241, 0.75))' : 'drop-shadow(0 0 3px rgba(255, 215, 0, 0.6))'
                                                          }}
                                                          title="Click to view Strategy Advisor Blueprint"
                                                        >
                                                          {medalLt}
                                                        </span>
                                                      ) : ''}D1-W1
                                                    </PillTf>
                                                    <PillIndicator $bias={row.status_long_term || 'Neutral'} theme={theme}>
                                                      <PillArrow $tier={intLt.tier}>{intLt.arrows}</PillArrow>
                                                    </PillIndicator>
                                                  </HorizonPill>
                                                </ScoreTooltip>
                                              </HorizonSummaryGroup>
                                            );
                                          })()}

                                          {isEditMode ? (
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                              <EditableCell
                                                type="textarea"
                                                placeholder="Macro synthesis / headline..."
                                                value={row.Headline}
                                                onChange={(val) => handleCellUpdate(row, 'Headline', val)}
                                              />
                                            </div>
                                          ) : (
                                            <ConclusionText>{row.Headline}</ConclusionText>
                                          )}

                                          {!isEditMode && (
                                            <span style={{ marginLeft: 'auto', fontSize: '12px', opacity: 0.5 }}>
                                              {isExpanded ? 'Hide Details' : 'Show Details'}
                                            </span>
                                          )}
                                        </ConclusionContainer>
                                      </Td>
                                    </Tr>
                                  )}
                                </Draggable>
                              );
                            })
                          )}
                          {provided.placeholder}
                        </tbody>
                      )}
                    </Droppable>

                    {/* Analysis Rows (Details) - Toggleable */}
                    {isExpanded && (
                      <Droppable droppableId={`analysis-${instrument}`}>
                        {(provided) => (
                          <tbody ref={provided.innerRef} {...provided.droppableProps}>
                            {analysisRows.length === 0 ? (
                              <Tr>
                                <Td colSpan={isEditMode ? 10 : 8} style={{ textAlign: "center", fontStyle: "italic", color: "#607d8b", padding: "12px" }}>
                                  No details
                                </Td>
                              </Tr>
                            ) : (
                              analysisRows.map((row, index) => (
                                <Draggable key={`analysis-${instrument}-${index}`} draggableId={`analysis-${instrument}-${index}`} index={index} isDragDisabled={!isEditMode}>
                                  {(provided, snapshot) => (
                                    <Tr
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      onClick={() => handleRowClick(row)}
                                      style={{
                                        ...provided.draggableProps.style,
                                        background: isEditMode && selectedRows.has(row) ? 'rgba(128, 203, 196, 0.1)' : snapshot.isDragging ? 'rgba(128, 203, 196, 0.15)' : 'transparent',
                                        cursor: isEditMode ? 'grab' : 'default',
                                        display: 'table-row'
                                      }}
                                    >
                                      {isEditMode && (
                                        <Td style={{ width: '40px', textAlign: 'center' }}>
                                          <CustomCheckbox
                                            checked={selectedRows.has(row)}
                                            onChange={() => handleRowClick(row)}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </Td>
                                      )}
                                      {isEditMode && (
                                        <Td style={{ width: '50px', textAlign: 'center' }}>
                                          <TdSmall style={{ opacity: 0.5 }}>{row.id}</TdSmall>
                                        </Td>
                                      )}

                                      <Td style={{ minWidth: '480px', whiteSpace: 'normal' }}>
                                        {isEditMode ? (
                                          <EditableCell type="textarea" value={row.Headline} onChange={(val) => handleCellUpdate(row, 'Headline', val)} />
                                        ) : (
                                          row.Headline
                                        )}
                                      </Td>
                                      <Td style={{ width: '95px', minWidth: '95px' }}>
                                        {isEditMode ? (
                                          <EditableCell value={row.Date} onChange={(val) => handleCellUpdate(row, 'Date', val)} />
                                        ) : (
                                          <TdSmall>{row.Date}</TdSmall>
                                        )}
                                      </Td>
                                      <Td style={{ minWidth: '440px', whiteSpace: 'normal' }}>
                                        {isEditMode ? (
                                          <EditableCell type="textarea" value={row.Interpretation} onChange={(val) => handleCellUpdate(row, 'Interpretation', val)} />
                                        ) : (
                                          <span style={{ color: 'inherit' }}>{row.Interpretation}</span>
                                        )}
                                      </Td>
                                      <Td style={{ width: '85px', minWidth: '85px', textAlign: 'center' }}>
                                        {isEditMode ? (
                                          <EditableCell value={row.Impact} onChange={(val) => handleCellUpdate(row, 'Impact', val)} />
                                        ) : (
                                          <ImpactBadge level={row.Impact}>{row.Impact}</ImpactBadge>
                                        )}
                                      </Td>
                                      <Td style={{ width: '80px', minWidth: '80px', textAlign: 'center' }}>
                                        {isEditMode ? (
                                          <EditableCell type="select" options={["Bullish", "Bearish", "Neutral"]} value={row.Direction} onChange={(val) => handleCellUpdate(row, 'Direction', val)} />
                                        ) : (
                                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                            {row.Direction === "Bullish" ? (
                                              <ArrowUpward style={{ color: tokens(theme.palette.mode).greenAccent[500], fontSize: '20px' }} />
                                            ) : row.Direction === "Bearish" ? (
                                              <ArrowDownward style={{ color: tokens(theme.palette.mode).redAccent[500], fontSize: '20px' }} />
                                            ) : row.Direction === "Neutral" ? (
                                              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: tokens(theme.palette.mode).grey[300] }} />
                                            ) : (
                                              <span style={{ color: tokens(theme.palette.mode).grey[500] }}>-</span>
                                            )}
                                          </div>
                                        )}
                                      </Td>
                                      <Td style={{ width: '80px', minWidth: '80px', textAlign: 'center' }}>
                                        {isEditMode ? (
                                          <EditableCell type="select" options={["", "Short Term", "Middle Term", "Long Term"]} value={row.Horizon || row.Duration} onChange={(val) => {
                                            handleCellUpdate(row, 'Horizon', val);
                                            handleCellUpdate(row, 'Duration', val);
                                          }} />
                                        ) : (
                                          <span style={{ fontSize: '11px', fontWeight: 600, color: tokens(theme.palette.mode).grey[300] }}>
                                            {(row.Horizon || row.Duration) ? (row.Horizon || row.Duration).replace(' Term', '') : "-"}
                                          </span>
                                        )}
                                      </Td>
                                      <Td style={{ width: '110px', minWidth: '110px', color: theme.palette.mode === 'dark' ? '#82aaff' : '#0288d1', whiteSpace: 'normal' }}>
                                        {isEditMode ? (
                                          <EditableCell value={row.Assets} onChange={(val) => handleCellUpdate(row, 'Assets', val)} />
                                        ) : (
                                          <span style={{ color: 'inherit' }}>{row.Assets}</span>
                                        )}
                                      </Td>
                                      <Td style={{ width: '110px', minWidth: '110px', whiteSpace: 'normal' }}>
                                        {isEditMode ? (
                                          <EditableCell value={row.Sources} onChange={(val) => handleCellUpdate(row, 'Sources', val)} />
                                        ) : (
                                          <TdSmall>{row.Sources}</TdSmall>
                                        )}
                                      </Td>
                                    </Tr>
                                  )}
                                </Draggable>
                              ))
                            )}
                            {provided.placeholder}
                          </tbody>
                        )}
                      </Droppable>
                    )}

                    {/* Spacer Row */}
                    <tbody>
                      <Tr style={{ height: '32px', background: 'transparent', border: 'none' }}>
                        <Td colSpan={isEditMode ? 8 : 6} style={{ border: 'none' }} />
                      </Tr>
                    </tbody>
                  </React.Fragment>
                );
              })
            ) : (
              <tbody>
                <Tr>
                  <Td colSpan={isEditMode ? 8 : 6} style={{ textAlign: "center", padding: "20px", color: "#607d8b" }}>
                    No data available for this category.
                  </Td>
                </Tr>
              </tbody>
            )}
          </DragDropContext>
        </Table>
      </TableWrapper>

      {
        showScrollX && (
          <ScrollRail ref={scrollRailXRef}>
            <ScrollThumb
              width={thumbWidth}
              left={thumbLeft}
              onMouseDown={handleMouseDownX}
            />
          </ScrollRail>
        )
      }

      {
        showScrollY && (
          <ScrollRailVertical ref={scrollRailYRef}>
            <ScrollThumbVertical
              height={thumbHeight}
              top={thumbTop}
              onMouseDown={handleMouseDownY}
            />
          </ScrollRailVertical>
        )
      }

      {
        rowToDelete && (
          <ModalOverlay>
            <ModalContent>
              <h3 style={{ color: '#fff', marginTop: 0 }}>Confirm Deletion</h3>
              <p style={{ color: '#b0bec5' }}>
                Are you sure you want to delete {Array.isArray(rowToDelete) ? rowToDelete.length : 1} row{Array.isArray(rowToDelete) && rowToDelete.length > 1 ? 's' : ''}?
                This action cannot be undone.
              </p>
              <ButtonRow>
                <Btn onClick={() => setRowToDelete(null)}>Cancel</Btn>
                <Btn primary onClick={confirmDelete} style={{ background: '#ef5350', borderColor: '#ef5350' }}>Delete</Btn>
              </ButtonRow>
            </ModalContent>
          </ModalOverlay>
        )
      }

      {
        selectedAdvisorRow && (
          <StrategyAdvisorModal
            row={selectedAdvisorRow}
            onClose={() => setSelectedAdvisorRow(null)}
            theme={theme}
          />
        )
      }
    </Panel >
  );
};

export default MarketOverviewTable;
